import { useCallback, useEffect, useMemo, useState } from 'react'
import { message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { useTranslation } from 'src/utils/i18n'
import { getPaymentInfo, upgradePayment } from 'src/@api/dashboard'
import { resolveUpgradeDeviceId } from 'src/utils/resolveUpgradeDeviceId'
import { refreshSubscriptionExpire } from 'src/utils/refreshSubscriptionExpire'
import { changeExtremeService } from 'src/store/apps/Common'
import { notifyExtremeUpgraded } from 'src/constants/extremeUpgrade'
import { navigateTo } from 'src/utils/navigate'

const defaultPaymentInfo = {
  card: null,
  months: 1,
  price: 49.98,
  upgradePrice: 44.98
}

const parseMonths = name => {
  const match = typeof name === 'string' ? name.match(/\d+/g) : null

  return match ? Number(match[0]) : 1
}

/** 月均价：向下取整到 2 位小数（不用四舍五入） */
const formatMoney = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0.00'

  return (Math.floor(n * 100 + Number.EPSILON) / 100).toFixed(2)
}

const useExtremeUpgradePayment = ({ enabled, onPaymentFallback }) => {
  const { locale } = useTranslation('common')
  const dispatch = useDispatch()
  const [messageApi, contextHolder] = message.useMessage()
  const selectedDeviceId = useSelector(state => state.Common.deviceUser)
  const [paymentInfo, setPaymentInfo] = useState(defaultPaymentInfo)
  const [loading, setLoading] = useState(false)
  const [paying, setPaying] = useState(false)

  const fetchPaymentInfo = useCallback(async () => {
    const id = resolveUpgradeDeviceId(selectedDeviceId)

    if (!id) {
      setPaymentInfo(defaultPaymentInfo)

      return
    }

    setLoading(true)

    try {
      const result = await getPaymentInfo({ params: { id } })
      const data = result.data

      if (data) {
        const card = typeof data.card === 'string' ? data.card.trim() : ''
        const months = parseMonths(data.name)
        const upgradePrice = Math.ceil((Number(data.price) + Number(data.setup)) * 100) / 100
        const price = Math.ceil(((Number(data.price) + Number(data.setup)) / 0.9) * 100) / 100

        setPaymentInfo({
          card: card || null,
          months,
          price,
          upgradePrice,
          expireTime: dayjs().add(Number(data.valid_days), 'day').format('YYYY-MM-DD')
        })
      }
    } catch (error) {
      setPaymentInfo(defaultPaymentInfo)
      console.error(error.message)
    } finally {
      setLoading(false)
    }
  }, [selectedDeviceId])

  useEffect(() => {
    if (!enabled) return

    fetchPaymentInfo()
  }, [enabled, fetchPaymentInfo])

  const getCheckoutHref = useCallback(
    id => {
      const currentId = id ?? resolveUpgradeDeviceId(selectedDeviceId)
      const lang = !locale || locale === '' ? 'en' : locale === 'it-ch' ? 'it' : locale
      const clientUrl = (process.env.NEXT_PUBLIC_CLIENT_URL || '').replace(/\/$/, '')

      return lang === 'en'
        ? `${clientUrl}/payment-2?id=${currentId}`
        : `${clientUrl}/${lang}/payment-2?id=${currentId}`
    },
    [locale, selectedDeviceId]
  )

  const pricing = useMemo(() => {
    const months = paymentInfo.months || 1
    const monthlyDiscounted = formatMoney(paymentInfo.upgradePrice / months)
    const monthlyOriginal = formatMoney(paymentInfo.price / months)

    return {
      months,
      monthlyDiscounted,
      monthlyOriginal
    }
  }, [paymentInfo])

  const handleUnlock = async () => {
    const id = resolveUpgradeDeviceId(selectedDeviceId)

    if (!id) {
      messageApi.error('Current device is missing')

      return
    }

    if (!paymentInfo.card) {
      navigateTo(getCheckoutHref(id))

      return
    }

    setPaying(true)

    try {
      const result = await upgradePayment({ id })

      if (Number(result.code) === 0) {
        await refreshSubscriptionExpire({ dispatch })
        dispatch(changeExtremeService(true))
        // spyx 对齐：先 USER_SUMMARY 预缓冲，再广播让本页拉详情读 advanced
        await notifyExtremeUpgraded()
        messageApi.success(result.msg || 'Upgrade successful')
      } else {
        onPaymentFallback?.()
      }
    } catch (error) {
      console.error(error.message)
      onPaymentFallback?.()
    } finally {
      setPaying(false)
    }
  }

  return {
    contextHolder,
    handleUnlock,
    loading,
    paying,
    paymentInfo,
    pricing
  }
}

export default useExtremeUpgradePayment
